import { supabase } from './supabase'
import type { ReminderState } from './reminder-schedule'

/**
 * Reads/writes for the monthly "Check Leaderboard" pop-up. Same thin-wrapper
 * shape as `milestonesettings.ts`/`tasks.ts`: it imports `supabase`, so the
 * rules live in `reminder-schedule.ts` (pure, tested) and this file only moves
 * rows. State is kept in the database, not the browser, so marking it done on
 * her laptop also clears it on her phone.
 */

export async function loadReminderState(
  reminder: string,
  period: string,
): Promise<{ state: ReminderState; error: string | null }> {
  const { data, error } = await supabase
    .from('reminder_state')
    .select('period, done_at, snoozed_until')
    .eq('reminder', reminder)
    .eq('period', period)
    .maybeSingle()
  if (error) return { state: null, error: error.message }
  return { state: (data as ReminderState) ?? null, error: null }
}

export async function snoozeReminder(
  reminder: string,
  period: string,
  minutes: number,
  userId: string | null,
): Promise<{ error: string | null }> {
  const until = new Date(Date.now() + minutes * 60_000).toISOString()
  const { error } = await supabase
    .from('reminder_state')
    .upsert(
      { reminder, period, snoozed_until: until, updated_at: new Date().toISOString(), updated_by: userId },
      { onConflict: 'reminder,period' },
    )
  return { error: error ? error.message : null }
}

export async function completeReminder(
  reminder: string,
  period: string,
  userId: string | null,
): Promise<{ error: string | null }> {
  const now = new Date().toISOString()
  const { error } = await supabase
    .from('reminder_state')
    .upsert(
      { reminder, period, done_at: now, done_by: userId, updated_at: now, updated_by: userId },
      { onConflict: 'reminder,period' },
    )
  return { error: error ? error.message : null }
}
